import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@arco-design/web-react';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import { SecureStringGenerator } from '@/utils';
import { IconDelete } from '@arco-design/web-react/icon';

interface LoginAccountInputProps {
  value: string;
  onChange: (value: string) => void;
  formRef?: any;
  autoFillPassword?: (password: string) => void;
}

const LoginAccountInput: React.FC<LoginAccountInputProps> = ({ 
  value, 
  onChange, 
  formRef,
  autoFillPassword 
}) => {
  // 状态：账号输入框的内容
  const [phoneInputValue, setPhoneInputValue] = useState(value || '');
  // 确保phoneInputValue始终是字符串类型
  const safePhoneInputValue = phoneInputValue || '';
  // 状态：是否显示账号提示框
  const [showTooltip, setShowTooltip] = useState(false);
  // 状态：是否在 electron 环境中
  const [isElectron, setIsElectron] = useState(false);
  // 状态：存储的登录账号列表
  const [savedLogins, setSavedLogins] = useState<any[]>([]);
  // 账号输入框的 ref
  const phoneInputRef = useRef(null);

  // 检测是否在 electron 环境中
  useEffect(() => {
    const checkIsElectron = () => {
      return window && window.electron;
    };
    setIsElectron(checkIsElectron());
  }, []);

  // 从 Cookie 加载保存的登录数据
  useEffect(() => {
    if (Cookies.get('Logindata')) {
      const encryptedData = Cookies.get('Logindata');

      try {
        // 解密数据
        const bytes = CryptoJS.AES.decrypt(
          encryptedData,
          SecureStringGenerator.generate(16)
        );

        // 将解密后的字节转换为 UTF-8 字符串
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);

        // 检查解密后的数据是否是有效的 UTF-8 字符串
        if (decrypted) {
          try {
            // 解析解密后的 JSON 字符串
            const data = JSON.parse(decrypted);
            // 检查是否为数组，如果不是则转换为数组
            const logins = Array.isArray(data) ? data : [data];
            // 过滤掉无效的登录项，确保每个项都有phone_number属性
            const validLogins = logins.filter(item => item && item.phone_number && typeof item.phone_number === 'string');
            setSavedLogins(validLogins);
          } catch (error) {
            console.error('解析解密后的数据时出错:', error);
          }
        } else {
          console.error('解密后的数据不是有效的 UTF-8 字符串。');
        }
      } catch (error) {
        console.error('解密数据时出错:', error);
      }
    }
  }, []);

  // 当外部传入的 value 变化时，更新内部状态
  useEffect(() => {
    setPhoneInputValue(value);
  }, [value]);

  // 点击账号输入框时显示 Tooltip
  const handlePhoneInputClick = () => {
    const filtered = savedLogins.filter(login => 
      typeof login?.phone_number === 'string' && 
      login.phone_number.toLowerCase().includes(safePhoneInputValue.toLowerCase())
    );
    if (isElectron && filtered.length > 0) {
      setShowTooltip(true);
    }
  };

  // 点击 Tooltip 中的账号时自动填入账号密码
  const handleLoginSelect = (loginData: any) => {
    onChange(loginData.phone_number);
    if (formRef && formRef.current) {
      formRef.current.setFieldValue('password', loginData.password);
    }
    if (autoFillPassword) {
      autoFillPassword(loginData.password);
    }
    setShowTooltip(false);
  };

  // 删除保存的账号
  const handleDeleteAccount = (phoneNumber: string) => {
    // 从保存的账号列表中移除指定账号
    const updatedLogins = savedLogins.filter(login => login.phone_number !== phoneNumber);
    
    // 更新状态
    setSavedLogins(updatedLogins);
    
    // 将更新后的账号列表重新加密保存到 Cookie
    try {
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(updatedLogins),
        SecureStringGenerator.generate(16)
      ).toString();
      
      // 根据是否是 Electron 环境设置不同的 Cookie 过期时间
      const options: any = {
        path: '/',
        expires: isElectron ? 30 : 1 // Electron 环境保存 30 天，非 Electron 环境保存 1 天
      };
      
      Cookies.set('Logindata', encryptedData, options);
    } catch (error) {
      console.error('保存删除后的账号列表失败:', error);
    }
  };

  // 点击外部区域时关闭 Tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTooltip) {
        // 检查点击是否在账号输入框或 Tooltip 内
        let phoneInputElement = null;
        const tooltip = document.querySelector('.login-tooltip');
        
        // 获取真实的DOM元素
        if (phoneInputRef.current) {
          // 如果是DOM元素直接使用
          if (typeof phoneInputRef.current.contains === 'function') {
            phoneInputElement = phoneInputRef.current;
          }
          // 如果是组件实例，尝试获取内部的DOM元素
          else if (phoneInputRef.current.input && typeof phoneInputRef.current.input.contains === 'function') {
            phoneInputElement = phoneInputRef.current.input;
          }
        }
        
        // 检查点击是否在输入框或tooltip外
        const isClickOutsideInput = !phoneInputElement || !phoneInputElement.contains(event.target as Node);
        const isClickOutsideTooltip = !tooltip || !tooltip.contains(event.target as Node);
        
        if (isClickOutsideInput && isClickOutsideTooltip) {
          setShowTooltip(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  // 过滤后的账号列表
  const filteredLogins = savedLogins.filter(login => 
    typeof login?.phone_number === 'string' && 
    login.phone_number.toLowerCase().includes(safePhoneInputValue.toLowerCase())
  );

  return (
    <div style={{ position: 'relative' }}>
      <Input
        ref={phoneInputRef}
        style={{ fontSize: '16px' }}
        className="form-contorinput"
        autoComplete="username"
        allowClear={true}
        placeholder="请输入账号"
        value={phoneInputValue}
        onClick={handlePhoneInputClick}
        onChange={(inputValue) => {
          setPhoneInputValue(inputValue);
          onChange(inputValue);
          const filtered = savedLogins.filter(login => 
            typeof login?.phone_number === 'string' && 
            login.phone_number.toLowerCase().includes((inputValue || '').toLowerCase())
          );
          if (isElectron && filtered.length > 0) {
            setShowTooltip(true);
          } else {
            setShowTooltip(false);
          }
        }}
      />
      {/* 自定义账号提示框 */}
      {showTooltip && filteredLogins.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '5px',
            backgroundColor: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 10000,
            fontSize: '14px',
            color: '#1d2129',
            boxSizing: 'border-box',
          }}
          className="login-tooltip arco-tooltip"
        >
          {/* 三角指示器 */}
          <div
            style={{
              position: 'absolute',
              top: '-27px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '0',
              height: '0',
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderBottom: '7px solid #d9d9d9',
              zIndex: -1,
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              top: '-26px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '0',
              height: '0',
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderBottom: '7px solid #fff',
              zIndex: 1,
            }}
          ></div>
          {/* 账号列表 */}
          <div style={{ padding: '4px 0' }}>
            {filteredLogins.map((login, index) => (
              <div
                key={index}
                style={{ 
                  padding: '8px 16px', 
                  cursor: 'pointer', 
                  backgroundColor: '#fff',
                  transition: 'all 0.2s ease',
                  lineHeight: '18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onClick={() => handleLoginSelect(login)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f5ff'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
              >
                <div>
                  <div style={{ color: '#1d2129', fontSize: '14px', marginBottom: '4px' }}>
                    {login.phone_number}
                  </div>
                  {login.loginTime && (
                    <div style={{ color: '#86909c', fontSize: '12px' }}>
                      最近登录：{login.loginTime}
                    </div>
                  )}
                </div>
                <IconDelete
                  style={{ color: '#86909c', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation(); // 阻止事件冒泡，避免触发账号选择
                    handleDeleteAccount(login.phone_number);
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ff4d4f'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#86909c'}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginAccountInput;
